import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, ArrowRight, Inbox } from "lucide-react";
import api from "../../lib/api";
import { StatusBadge, StatusProgress } from "../../components/StatusTools";

export default function PortalDashboard() {
  const [cases, setCases] = useState(null);

  useEffect(() => {
    api.get("/cases").then((r) => setCases(r.data)).catch(() => setCases([]));
  }, []);

  const active = (cases || []).filter((c) => !["completed", "final_delivered"].includes(c.status)).length;

  return (
    <div data-testid="portal-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">My Cases</h1>
          <p className="text-sm text-slate-500 mt-1">{cases ? `${cases.length} total · ${active} in progress` : "Loading…"}</p>
        </div>
        <Link to="/portal/new" data-testid="dashboard-new-case-btn"
          className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-sky-700 transition-colors">
          <FilePlus2 className="h-4 w-4" /> Submit a Case
        </Link>
      </div>

      {cases === null ? (
        <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>
      ) : cases.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-20 text-center" data-testid="cases-empty-state">
          <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-4" />
          <p className="font-display font-bold text-xl">No cases yet</p>
          <p className="text-sm text-slate-500 mt-2 mb-6">Submit your first aligner case and we'll take it from there.</p>
          <Link to="/portal/new" data-testid="empty-new-case-btn" className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-6 py-3 text-sm font-bold text-white hover:bg-sky-700">
            Submit your first case <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cases-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-semibold">Case ID</th>
                  <th className="px-5 py-3.5 font-semibold">Patient Ref</th>
                  <th className="px-5 py-3.5 font-semibold">Type</th>
                  <th className="px-5 py-3.5 font-semibold w-48">Progress</th>
                  <th className="px-5 py-3.5 font-semibold">Status</th>
                  <th className="px-5 py-3.5 font-semibold">Submitted</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`case-row-${c.case_id}`}>
                    <td className="px-5 py-4 font-mono font-semibold text-sky-700 whitespace-nowrap">
                      {c.case_id}
                      {c.first_free && <span className="ml-2 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid={`free-case-badge-${c.case_id}`}>Free</span>}
                    </td>
                    <td className="px-5 py-4">{c.patient_ref}</td>
                    <td className="px-5 py-4 text-slate-500">{c.case_type}</td>
                    <td className="px-5 py-4"><StatusProgress status={c.status} /></td>
                    <td className="px-5 py-4"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-4 text-slate-500 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      <Link to={`/portal/cases/${c.id}`} data-testid={`case-open-${c.case_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800">
                        Open <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
